import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Check, 
  Sparkles, 
  ArrowRight, 
  Shield, 
  Zap, 
  Heart,
  Clock
} from "lucide-react";

export const PricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const inclusions = [
    "NOVA — Contenu (posts, carrousels, newsletters)",
    "PRISME — Page de conversion éthique",
    "PORTAIL — Tunnel Stories Instagram",
    "ORBITE — Ads (si tu veux scaler)",
    "NOYAU — Protocoles entre séances",
    "FRACTURE — Coach anti-excuses",
    "TRAJECTOIRE — Plan 14 jours + accountability",
    "DOUBLE — \"Gilles H24\"",
    "BONUS: SPECTRE — Audit d'offre Hormozi",
  ];

  return (
    <section 
      ref={ref}
      className="section-padding bg-gradient-to-b from-mint-whisper via-white to-mint-whisper relative overflow-hidden"
      data-testid="pricing-section"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl" />
      </div>

      <div className="lumen-container relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="font-cinzel text-sm tracking-[0.3em] text-gold-metallic uppercase mb-4 block">
              Pourquoi ça marche
            </span>
            <h2 className="section-title font-cormorant text-text-main mb-6">
              Moins d'effort. Plus de cohérence. <span className="italic text-emerald-700">Plus de résultats.</span>
            </h2>
            <p className="font-manrope text-text-muted max-w-2xl mx-auto">
              LUMEN te donne des structures, des scripts, des checklists, des commandes simples et un cadre de posture.
            </p>
          </motion.div>

          {/* Time saved highlight */}
          <motion.div
            className="glass-card p-6 mb-12 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center justify-center gap-4">
              <Clock className="w-8 h-8 text-emerald-600" />
              <p className="font-cormorant text-2xl text-text-main">
                Une <strong>team d'assistants IA</strong> qui te rend <strong className="text-emerald-700">≈ 15h/semaine</strong>
              </p>
            </div>
          </motion.div>

          {/* Pricing card */}
          <motion.div
            className="price-card p-8 md:p-12"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {/* Header */}
            <div className="text-center mb-10">
              <span className="font-cinzel text-sm tracking-[0.3em] text-text-muted uppercase block mb-2">
                Paiement unique
              </span>
              <h3 className="font-cormorant text-2xl text-text-main mb-4">
                Accès à vie
              </h3>
              
              {/* Price display */}
              <div className="flex items-center justify-center mb-4">
                <span className="text-5xl md:text-6xl font-cormorant font-bold text-emerald-700">333€</span>
              </div>
              <p className="font-manrope text-sm text-text-muted">
                Accès immédiat • Copiers-collers prêts • Actionnable en 7 jours
              </p>
            </div>

            {/* Inclusions */}
            <div className="grid md:grid-cols-2 gap-3 mb-10">
              {inclusions.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -10 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.3, delay: 0.5 + index * 0.05 }}
                >
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className={`font-manrope text-sm ${item.includes('BONUS') ? 'text-amber-600 font-medium' : 'text-text-main'}`}>
                    {item}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center">
              <motion.a 
                href="https://gillesdelieuze.com/store-checkout-03/"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-primary group text-lg px-12 py-5"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="pricing-cta-button"
              >
                <Sparkles className="w-5 h-5" />
                Je prends LUMEN
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </motion.a>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-text-muted">
                <span className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-500" />
                  Paiement sécurisé
                </span>
                <span className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-emerald-500" />
                  Accès immédiat
                </span>
                <span className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-emerald-500" />
                  Support inclus
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
